/**
 * "Create User" dialog used by the Admin page.
 *
 * Extracted from AdminPage so the page-level component stays focused on
 * state + list rendering. No business decisions are made here — the
 * caller wires `onCreated` to refresh the user list after a successful
 * POST. The starter password is shown to the admin as plain text on
 * purpose: there is no email pipeline yet and the admin needs to
 * forward credentials manually.
 */
import { useState } from "react";
import { api } from "@/lib/api";
import { apiError } from "@/lib/adminFormat";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
  DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";
import { Plus } from "lucide-react";
import { toast } from "sonner";

const EMPTY_FORM = { full_name: "", email: "", password: "", role: "technician" };

export default function CreateUserDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/register", {
        ...form,
        email: form.email.trim().toLowerCase(),
      });
      toast.success(`User ${form.email} created — share the password with them directly.`);
      setOpen(false);
      setForm(EMPTY_FORM);
      onCreated();
    } catch (err) {
      toast.error(apiError(err, "Failed to create user"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          className="bg-red-700 hover:bg-red-600 font-display tracking-wider"
          data-testid="open-create-user"
        >
          <Plus className="w-4 h-4 mr-2" />
          CREATE USER
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-slate-950 border-slate-800 text-slate-100" data-testid="create-user-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-wider">Create User</DialogTitle>
          <DialogDescription className="text-slate-400">
            No email is sent — type a starter password and share it with the new user directly.
            They can change it from their profile after first login.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label htmlFor="cu-name" className="text-xs uppercase tracking-wider text-slate-400">Full name</Label>
            <Input
              id="cu-name" required value={form.full_name}
              onChange={(e) => setForm({ ...form, full_name: e.target.value })}
              className="bg-slate-900 border-slate-700 mt-1"
              data-testid="create-user-name"
            />
          </div>
          <div>
            <Label htmlFor="cu-email" className="text-xs uppercase tracking-wider text-slate-400">Email</Label>
            <Input
              id="cu-email" type="email" required value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="bg-slate-900 border-slate-700 mt-1"
              data-testid="create-user-email"
            />
          </div>
          <div>
            <Label htmlFor="cu-pw" className="text-xs uppercase tracking-wider text-slate-400">Starter password</Label>
            <Input
              id="cu-pw" type="text" required minLength={8} value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              placeholder="At least 8 chars, 1 letter + 1 number"
              className="bg-slate-900 border-slate-700 mt-1 font-mono"
              autoComplete="new-password"
              data-testid="create-user-password"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-400">Role</Label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger className="bg-slate-900 border-slate-700 mt-1" data-testid="create-user-role">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="technician">Technician</SelectItem>
                <SelectItem value="foreman">Foreman</SelectItem>
                <SelectItem value="pm">Project Manager</SelectItem>
                <SelectItem value="admin">Administrator</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button
              type="submit" disabled={busy}
              className="bg-emerald-700 hover:bg-emerald-600 font-display tracking-wider"
              data-testid="create-user-submit"
            >
              {busy ? "Creating..." : "CREATE"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
